import { useMemo } from 'react';
import { AvailabilityStaff, DayAvailability, SlotBusyType } from 'services/caller-id';
import aptSelectors from 'store/appt/selectors';
import { useAppSelector } from 'store/hooks';
import shopSelectors from 'store/shop/selectors';
import { momentTimezone } from 'utils/time';
import { ANYONE_ID } from '../CalendarUI/anyone';
import { SLOT_STEP_MIN } from './use-booking-form';

// Pastel initials-dot palettes cycled across real staff/services (prototype look).
export const TAG_PALETTES = [
  { bg: '#FFEAD9', fg: '#C24500' },
  { bg: '#E2EEFE', fg: '#003BC8' },
  { bg: '#EEE7FB', fg: '#6D3BC8' },
  { bg: '#FDEBC2', fg: '#8A6100' },
  { bg: '#DCF3DF', fg: '#0C7714' },
  { bg: '#FCE7F3', fg: '#BE185D' },
];

export const initialsOf = (name: string): string =>
  name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

// Availability for the calendar's currently loaded day, derived from the same
// store data OwnerCalendar renders (appointments + block hours + staff list) —
// no extra API. ANYONE-column items (staffId '0') don't block a specific staff.
export const useDayAvailability = (): DayAvailability => {
  const appointments = useAppSelector(aptSelectors.appointments);
  const blockHours = useAppSelector(aptSelectors.blockHours);
  const staffList = useAppSelector(shopSelectors.staff);

  return useMemo(() => {
    const staff: AvailabilityStaff[] = staffList
      .filter(s => String(s.id) !== ANYONE_ID)
      .map((s, i) => {
        const palette = TAG_PALETTES[i % TAG_PALETTES.length];
        const label = s.extraData?.nickName || s.name;
        return {
          id: String(s.id),
          label,
          tag: initialsOf(label),
          tagBg: palette.bg,
          tagFg: palette.fg,
        };
      });

    const busy: Record<string, Record<number, SlotBusyType>> = {};
    const items: { staffId?: string; startTime: string; endTime: string; kind: SlotBusyType }[] = [
      ...appointments.map(a => ({ staffId: a.staffId, startTime: a.startTime, endTime: a.endTime, kind: 'appt' as const })),
      ...blockHours.map(b => ({ staffId: b.staffId, startTime: b.startTime, endTime: b.endTime, kind: 'block' as const })),
    ];
    items.forEach(item => {
      if (!item.staffId || item.staffId === ANYONE_ID) return;
      const start = momentTimezone(item.startTime);
      const end = momentTimezone(item.endTime);
      if (!start.isValid() || !end.isValid()) return;
      const startMin = start.hours() * 60 + start.minutes();
      const endMin = end.hours() * 60 + end.minutes();
      const map = busy[item.staffId] ?? (busy[item.staffId] = {});
      // mark every 30-min slot the item overlaps
      const firstSlot = Math.floor(startMin / SLOT_STEP_MIN) * SLOT_STEP_MIN;
      for (let slot = firstSlot; slot < endMin; slot += SLOT_STEP_MIN) {
        // block hours win over appointments on the same slot
        if (map[slot] !== 'block') map[slot] = item.kind;
      }
    });

    return { staff, busy };
  }, [appointments, blockHours, staffList]);
};
